import { inject, Injectable } from '@angular/core';
import { HumanReadableDateTimePipe } from '../shared/human-readable-date-time.pipe';
import { CallDurationPipe } from '../shared/call-duration.pipe';
import { CallTypeCodePipe } from '../shared/call-type-code.pipe';
import { TranslateService } from '@ngx-translate/core';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { DetailedCallRecord } from '../../../shared/models/generated/ziroModels';
import { CallRecordsParams } from '../shared/detailed-call-records-report.resource';
import { CallRecordSearchType } from './pstn-call-record-query-editor-field/pstn-call-record-query-editor-field.component';
import { CallRecordsRow } from './pstn-call-record-finder.component';
import { CallDirection, CallToFromUtil } from '../shared/call-to-from-util';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';

@Injectable()
export class CallRecordsRowMapperService {
  private _callDurationPipe = inject(CallDurationPipe);
  private _callTypeCodePipe = inject(CallTypeCodePipe);
  private _dateTimePipe = inject(HumanReadableDateTimePipe);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _translateService = inject(TranslateService);

  mapRows(args: {
    records: DetailedCallRecord[];
    query: CallRecordsParams;
    searchBy: CallRecordSearchType;
  }): CallRecordsRow[] {
    return args.records.map((record) => this._mapRow(record, args.query, args.searchBy));
  }

  private _mapRow(
    record: DetailedCallRecord,
    query: CallRecordsParams,
    searchBy: CallRecordSearchType,
  ): CallRecordsRow {
    const cells = CallToFromUtil.computeCells({
      record,
      selectedDirection: query.direction,
      searchBy,
      user: {
        userPrincipalName: record.userPrincipalName,
        displayName: record.displayName,
      },
      telephoneNumberFilter: (telephoneNumber: string) => this._telephoneNumberFilter.transform(telephoneNumber),
    });

    const searchByNumber = !!query.number;
    const fromHtml = this._buildToFromHtml(cells.from, cells.fromSubtext, searchByNumber);
    const toHtml = this._buildToFromHtml(cells.to, cells.toSubtext, searchByNumber);
    const startDateCellHtml = record.startDateTime
      ? this._dateTimePipe.transform(record.startDateTime)
      : this._translateService.instant('tkey;reports.microsoft.pstn_call_record_finder.start_date.html.content', {
          failureDateTime: this._dateTimePipe.transform(record.failureDateTime),
        });
    const durationSeconds = record?.duration ?? 0;
    const durationCellHtml = record.failureDateTime
      ? this._translateService.instant('tkey;reports.microsoft.pstn_call_record_finder.duration_failed.html.content')
      : this._translateService.instant('tkey;reports.microsoft.pstn_call_record_finder.duration.html.content', {
          duration: this._callDurationPipe.transform(String(durationSeconds)),
        });
    const iconByDirection: Partial<Record<CallDirection, string>> = {
      [CallDirection.INBOUND]: ZiroIcons.INCOMING_CALLS_SMALL,
      [CallDirection.OUTBOUND]: ZiroIcons.OUTGOING_CALLS_SMALL,
      [CallDirection.OTHER]: ZiroIcons.UNDEFINED,
    };
    const iconClass = iconByDirection[record.callDirection];
    const callTypeCodeHtml = `
            <span class="alert alert-inline alert-info">
              ${iconClass ? `<i class="${iconClass}"></i>` : ''}
              ${record.callTypeCode}
            </span>
          `;
    return {
      rowId: crypto.randomUUID(),
      startDateTime: `${startDateCellHtml}`,
      to: cells.toSubtext ? `<strong>${cells.to}</strong>` : cells.to,
      toSubtext: cells.toSubtext,
      from: cells.fromSubtext ? `<strong>${cells.from}</strong>` : cells.from,
      fromSubtext: cells.fromSubtext,
      duration: record.duration,
      callTypeCode: record.callTypeCode,
      hardcodedTooltips: {
        callTypeCode: this._getCallTypeTooltip(record.callTypeCode, record.callDirection),
      },
      html: {
        startDateTime: `<strong>${startDateCellHtml}</strong>`,
        from: record.callDirection !== CallDirection.OTHER ? fromHtml : '',
        to: record.callDirection !== CallDirection.OTHER ? toHtml : '',
        duration: durationCellHtml,
        callTypeCode: callTypeCodeHtml,
      },
    };
  }

  private _buildToFromHtml(top: string, subtext: string, searchByNumber: boolean): string {
    const isNumberOnly = !this._isBlank(top) && this._isBlank(subtext);

    if (!searchByNumber && isNumberOnly) {
      return this._renderToFromCell({
        top: subtext || ' ',
        bottom: top || ' ',
        boldTop: false,
      });
    }

    return this._renderToFromCell({
      top: top || ' ',
      bottom: subtext || ' ',
      boldTop: !this._isBlank(subtext),
    });
  }

  private _getCallTypeTooltip(callTypeCode: string, direction: CallDirection): string {
    if (direction === CallDirection.OTHER) {
      return this._translateService.instant(
        'tkey;reports.microsoft.pstn_call_record_finder.table.call_type_code.other',
      );
    }

    return this._translateService.instant(this._callTypeCodePipe.transform(callTypeCode));
  }

  private _isBlank(value?: string): boolean {
    return !value || value.trim() === '';
  }

  private _renderToFromCell(args: { top: string; bottom: string; boldTop?: boolean }): string {
    const topHtml = args.boldTop ? `<strong>${args.top}</strong>` : args.top;

    return `
      <ul class="list-unstyled">
        <li class="datatable-cell-primary">${topHtml}</li>
        <li class="datatable-cell-subtext">${args.bottom}</li>
      </ul>
    `;
  }
}
